/*
 * The step 1 form that goes on a customer's website: a registration, a postcode and (unless left out) a phone number and an email.
 * It is built and served by this app (one script, /lib/hubscrap.js), so the form's submission and the pages that follow it are all under
 * one app: the library sends the first step to the app it was loaded from and goes to the address the app answers with. Nothing about
 * addresses is configured on the page, and there is no stylesheet to load: the form is drawn in a shadow root with its own styles, so
 * the page's CSS cannot reach it and its CSS cannot reach the page.
 *
 *   <script src="https://forms.example.com/lib/hubscrap.js" defer></script>
 *   <div data-hubscrap-form="FORM_ID" data-workspace="WORKSPACE_ID" data-brand="BRAND_ID" data-affiliate="AFFILIATE_ID"></div>
 *
 * `data-hubscrap-form` is the form's id; `data-workspace` is the ScrapStack workspace that published it (its config is in that workspace's folder); both are required, here, in `addForm` or in `boot`; `data-brand` (optional) is the brand it is shown as; `data-affiliate` (optional) is the id of the
 * affiliate the visitor came from, kept against the lead so the CRM can link it; `data-type` is `scrap_car` (the default)
 * or `salvage`; `data-layout` is `vertical` (the default) or `horizontal`; `data-hide` lists fields to leave out (`email`, `phone`).
 * A form that is being tested (ScrapStack's test screen) is given `testToken`, which makes its leads tests: kept apart from real ones and never
 * sent on. It is only an option of `addForm`, never an attribute, and it is of no use without ScrapStack's signature.
 * `HubScrap.addForm(target, options)` does the same from script, and `HubScrap.boot({ workspaceId, formId, brandId, affiliateId, endpoint, debug })` sets the workspace, form, brand and affiliate (the last two optional) every form on the page falls back to (each form may name its own, so one page can hold several forms), and is otherwise for pointing the
 * library at another app (development). Calls can be made before the library has loaded (it may be loaded `async`, or by a tag manager):
 * `window.HubScrap = window.HubScrap || []; HubScrap.push(['addForm', '.here', { formId: 'FORM_ID' }])` is run when it arrives, and a
 * `push` after that runs at once. Forms added to the page later (a single-page app showing a route) are found and drawn by themselves,
 * and loading the library twice does no harm.
 *
 * The form wears the form's own look: it asks the app for the form's design tokens (the brand's colours, corner radii and font) and sets
 * them on itself as CSS variables. It stays hidden (keeping its space) until they arrive, for at most a third of a second, so it never
 * flashes the wrong colours; if they cannot be had it shows with its own look.
 *
 * It tells the page what happens, so the customer's analytics can count it: a bubbling `HUBSCRAP_FORMS_READY`, `HUBSCRAP_FORMS_SUBMIT`,
 * `HUBSCRAP_FORMS_SUCCESS` (cancelable: stop it and the page takes the visitor on itself) and `HUBSCRAP_FORMS_ERROR` event on the form's element,
 * with `detail` = { formId, brandId, affiliateId, formType, ... } and never what the visitor typed. Those always fire. Beyond them it tells the
 * page's tracking the same event, `HUBSCRAP_FORMS_{NAME}`, by the first of these the page has: Google Tag Manager (a `dataLayer` push), else
 * Google Analytics (`gtag('event', ...)`), else nothing (the browser events are all there is). The values are the plain snake_case ones the
 * renderer's events carry (`form_id`, `brand_id`, `affiliate_id`, `form_type`, `reference`). Every event name is letters, numbers and
 * underscores, so every tool takes it as it is. A redirect waits up to 0.4 s for the tracking it used to finish with `HUBSCRAP_FORMS_SUCCESS`.
 * `HubScrap.tracking()` says which it is: `tag-manager`, `analytics` or `browser`; only one is ever told, so an event is never counted twice, and
 * `HubScrap.ignored()` says when Analytics was left out because Tag Manager is there (`analytics`, else null).
 */
import styles from '../../css/library.css?inline'

const TYPES = ['scrap_car', 'salvage']
const SUBMIT_TEXT = 'Get Quote'
const BUSY_TEXT = 'Processing...'
const THEME_WAIT = 300 // ms the form stays hidden for its theme
const TAGS_WAIT = 400 // ms a redirect waits for the page's tags to finish
const GENERIC_ERROR = 'Something went wrong. Please check your connection and try again.'

// Where the library was loaded from is where it submits: read while the script runs, since later it is no longer the current script.
// Where `document.currentScript` is empty (a script put in by a bundler or a loader), the library's own tag is looked for.
const script = document.currentScript || [...document.querySelectorAll('script[src*="/lib/"][src*="hubscrap"]')].pop()
const settings = {
    endpoint: script && script.src ? new URL(script.src).origin : '',
    debug: false,
    // Set once by `boot`, for every form on the page that does not name its own.
    workspaceId: '',
    formId: '',
    brandId: '',
    affiliateId: '',
}

// Inline icons: resolution independent and no extra requests.
const svg = (size, body) =>
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`
const badge = (text) =>
    svg(44, `<circle cx="12" cy="12" r="10.5"/><text x="12" y="15.4" font-size="8.5" font-weight="700" text-anchor="middle" fill="#fff" stroke="none">${text}</text>`)

const FIELDS = {
    vrm: {
        icon: badge('GB'),
        placeholder: 'Vehicle Reg',
        type: 'text',
        autocomplete: 'off',
        hints: ' autocapitalize="characters" spellcheck="false"',
    },
    postcode: {
        icon: badge('UK'),
        placeholder: 'Postcode',
        type: 'text',
        autocomplete: 'postal-code',
        hints: ' autocapitalize="characters" spellcheck="false"',
    },
    phone: {
        icon: svg(30, '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z"/>'),
        placeholder: 'Phone',
        type: 'tel',
        autocomplete: 'tel',
        hints: ' inputmode="tel"',
    },
    email: {
        icon: svg(32, '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/>'),
        placeholder: 'Email',
        type: 'email',
        autocomplete: 'email',
        hints: ' inputmode="email" autocapitalize="off" spellcheck="false"',
    },
}

const OPTIONAL = ['phone', 'email']

const field = (key) => {
    const one = FIELDS[key]

    return `<div class="form-field-wrapper ${key}-field-wrapper"><div class="field-with-icon">` +
        `<div class="field-icon-wrapper ${key}-field-icon-wrapper">${one.icon}</div>` +
        `<div class="field-input-wrapper ${key}-field-input-wrapper">` +
        `<input class="field-input ${key}-field-input" type="${one.type}" name="${key}" placeholder="${one.placeholder}" aria-label="${one.placeholder}" aria-describedby="${key}-error" autocomplete="${one.autocomplete}"${one.hints || ''}>` +
        `</div></div><div class="field-error ${key}-field-error" id="${key}-error" role="alert"></div></div>`
}

const log = (...args) => settings.debug && console.log('[HubScrap]', ...args)

/**
 * The tracking the page has, asked each time as tags load late. Google Tag Manager first: a container on the page (or its `gtm.js` start in the
 * data layer), or a data layer with no `gtag` to say it is Analytics' own. Then Google Analytics (`gtag`). Else the browser events alone.
 */
function tracking() {
    const layer = Array.isArray(window.dataLayer)
    const containers = window.google_tag_manager && typeof window.google_tag_manager === 'object' ? Object.keys(window.google_tag_manager) : []
    const manager = containers.some((id) => /^GTM-/i.test(id)) || (layer && window.dataLayer.some((item) => item && item.event === 'gtm.js'))
    const analytics = typeof window.gtag === 'function'

    if (layer && (manager || !analytics)) return 'tag-manager'

    return analytics ? 'analytics' : 'browser'
}

/** The tracking the page has but that is left out, so an event is never counted twice: Analytics, when Tag Manager is there to be told first. */
const ignored = () => (tracking() === 'tag-manager' && typeof window.gtag === 'function' ? 'analytics' : null)

/** What the tracking is told of an event: its plain values as snake_case parameters (the renderer's names), never what the visitor typed and never an address. */
const parameters = (detail) =>
    Object.fromEntries(
        Object.entries(detail)
            .filter(([key, value]) => key !== 'url' && value !== null && typeof value !== 'object')
            .map(([key, value]) => [key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`), value]),
    )

/**
 * Tells the page something happened, as a bubbling DOM event (which crosses the shadow root) and then to the tracking the page has (see
 * `tracking`). `later` is what the tracking is asked to do when it is done (`success` waits for it before leaving the page).
 * Never what the visitor typed. Returns false if the page cancelled the event (only `success` can be).
 */
function emit(element, name, options, extra = {}, later = null) {
    const detail = {formId: options.formId, brandId: options.brandId || null, affiliateId: options.affiliateId || null, formType: options.formType, ...extra}
    const event = new CustomEvent(`HUBSCRAP_FORMS_${name.toUpperCase()}`, {bubbles: true, composed: true, cancelable: name === 'success', detail})
    const notCancelled = element.dispatchEvent(event)
    const channel = tracking()

    if (channel === 'tag-manager' && ignored()) log('Google Analytics was not told: Google Tag Manager is on this page and is told first.')

    // The same event the page just heard, told to its tracking; a redirect waits for the tracking to finish with it.
    const told = `HUBSCRAP_FORMS_${name.toUpperCase()}`
    const wait = later ? {eventCallback: later, eventTimeout: TAGS_WAIT} : {}

    if (channel === 'tag-manager') window.dataLayer.push({event: told, ...parameters(detail), ...wait})
    else if (channel === 'analytics') window.gtag('event', told, {...parameters(detail), ...(later ? {event_callback: later, event_timeout: TAGS_WAIT} : {})})

    return notCancelled
}

/** The form's styles, once per page where the browser allows (a constructable sheet is not a style tag, so a strict policy for styles is no obstacle). */
let sheet = null

function style(root) {
    if (typeof CSSStyleSheet !== 'undefined' && CSSStyleSheet.prototype.replaceSync && 'adoptedStyleSheets' in root) {
        if (!sheet) {
            sheet = new CSSStyleSheet()
            sheet.replaceSync(styles)
        }

        root.adoptedStyleSheets = [sheet]

        return
    }

    const tag = document.createElement('style')

    tag.textContent = styles
    root.appendChild(tag)
}

/** What a form needs, as `data-` attributes or an options object give it. */
function optionsOf(element, given = {}) {
    const data = element.dataset || {}
    const hide = given.disabledFields || (data.hide ? data.hide.split(',').map((one) => one.trim()) : [])

    return {
        formId: given.formId || data.hubscrapForm || settings.formId,
        workspaceId: String(given.workspaceId || data.workspace || settings.workspaceId).trim(),
        brandId: given.brandId || data.brand || settings.brandId,
        affiliateId: String(given.affiliateId || data.affiliate || settings.affiliateId).trim(),
        testToken: String(given.testToken || '').trim(),
        formType: (given.formType || data.type || TYPES[0]).toLowerCase(),
        horizontal: (given.formLayout || data.layout || 'vertical').toLowerCase() === 'horizontal',
        hide: hide.map((one) => String(one).toLowerCase()).filter((one) => OPTIONAL.includes(one)),
    }
}

function show(form, errors) {
    for (const key of Object.keys(errors)) {
        const target = form.querySelector(`.${key}-field-error`)
        const input = form.querySelector(`[name="${key}"]`)

        if (!target) continue

        target.textContent = Array.isArray(errors[key]) ? errors[key][0] : String(errors[key])
        target.style.display = 'block'
        input && input.setAttribute('aria-invalid', 'true')
    }
}

function clear(form) {
    form.querySelectorAll('.field-error').forEach((node) => {
        node.textContent = ''
        node.style.display = 'none'
    })
    form.querySelectorAll('[aria-invalid]').forEach((node) => node.removeAttribute('aria-invalid'))
}

async function submit(form, options) {
    const button = form.querySelector('button[type=submit]')
    const failure = form.querySelector('.form-error')
    const data = Object.fromEntries(new FormData(form))
    const post = {category: options.formType, form_id: options.formId, vrm: data.vrm, postcode: data.postcode, referrer_url: location.href}

    if (options.workspaceId) post.workspace_id = options.workspaceId
    if (options.brandId) post.brand_id = options.brandId
    if (options.affiliateId) post.affiliate_id = options.affiliateId
    if (options.testToken) post.test_token = options.testToken
    // An optional field that was left blank is not sent: the app refuses a blank one rather than treating it as not given.
    for (const key of OPTIONAL) if (data[key] !== undefined && String(data[key]).trim() !== '') post[key] = String(data[key]).trim()

    clear(form)
    failure.textContent = ''
    failure.style.display = 'none'
    button.disabled = true
    button.textContent = BUSY_TEXT
    emit(form, 'submit', options)

    try {
        const response = await fetch(`${settings.endpoint}/leads`, {
            method: 'POST',
            headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
            body: JSON.stringify(post),
        })
        const body = await response.json().catch(() => ({}))

        log(response.status, body)

        if (response.status === 201 && body.url) {
            proceed(form, options, body)

            return
        }

        if (response.status === 422 && body.errors) {
            show(form, body.errors)
            emit(form, 'error', options, {reason: 'invalid', fields: Object.keys(body.errors)})
        } else {
            failure.textContent = GENERIC_ERROR
            failure.style.display = 'block'
            emit(form, 'error', options, {reason: 'failed', status: response.status})
        }
    } catch (error) {
        log(error)
        failure.textContent = GENERIC_ERROR
        failure.style.display = 'block'
        emit(form, 'error', options, {reason: 'failed', status: 0})
    }

    button.disabled = false
    button.textContent = SUBMIT_TEXT
}

/**
 * The lead was taken: tell the page, then take the visitor on. The page may cancel the event and take them itself; if it has a
 * `dataLayer` the redirect waits (briefly) for its tags to fire, since leaving the page cuts them off.
 */
function proceed(form, options, body) {
    let gone = false
    const go = () => {
        if (gone) return
        gone = true
        location.href = body.url
    }
    const waits = tracking() !== 'browser'
    const proceeding = emit(form, 'success', options, {reference: body.reference || null, url: body.url}, waits ? go : null)

    if (!proceeding) return

    if (waits) setTimeout(go, TAGS_WAIT)
    else go()
}

/** The form's design tokens, once per form: the app's own CSS variables (`--color-brand`), which the form's stylesheet reads. */
const themes = new Map()

function theme(options) {
    const key = `${options.workspaceId}|${options.formId}|${options.brandId}`

    if (!themes.has(key)) {
        const params = new URLSearchParams()

        if (options.workspaceId) params.set('workspace', options.workspaceId)
        if (options.brandId) params.set('brand', options.brandId)

        const query = params.size ? `?${params}` : ''

        themes.set(
            key,
            fetch(`${settings.endpoint}/lib/theme/${encodeURIComponent(options.formId)}${query}`, {headers: {Accept: 'application/json'}})
                .then((response) => (response.ok ? response.json() : {}))
                .then((body) => body.variables || {})
                .catch(() => ({})),
        )
    }

    return themes.get(key)
}

/**
 * Sets the form's tokens on the form, and shows the form: it is held back (keeping its place on the page) until they have arrived or a
 * short wait is over, so it never flashes the wrong colours; with none to be had it shows as it is. Only the app's own variables, and
 * only plain values, are ever set.
 */
function wear(form, options) {
    const apply = theme(options).then((variables) => {
        for (const [name, value] of Object.entries(variables)) {
            if (/^--[a-z-]+$/.test(name) && typeof value === 'string' && !/[;{}<>]/.test(value)) form.style.setProperty(name, value)
        }
    })
    const wait = new Promise((resolve) => setTimeout(resolve, THEME_WAIT))

    form.classList.add('is-loading')
    Promise.race([apply, wait]).then(() => form.classList.remove('is-loading'))
}

/** Draws the form in an element, once. Returns whether it did. */
function mount(element, given) {
    if (element.hasAttribute('data-hubscrap-bound')) return false

    const options = optionsOf(element, given)

    if (!options.formId) {
        console.warn('HubScrap: a form needs its id (data-hubscrap-form, formId in addForm or in boot).')

        return false
    }

    if (!options.workspaceId) {
        console.warn('HubScrap: a form needs its workspace id (data-workspace, workspaceId in addForm or in boot).')

        return false
    }

    if (!TYPES.includes(options.formType)) {
        console.warn(`HubScrap: the form type must be one of ${TYPES.join(', ')}.`)

        return false
    }

    if (!settings.endpoint) {
        console.warn('HubScrap: the library could not tell where it was loaded from. Load it with a script tag, or call HubScrap.boot({ endpoint }).')

        return false
    }

    const layout = options.horizontal ? 'horizontal-layout' : 'vertical-layout'
    const keys = ['vrm', 'postcode', 'phone', 'email'].filter((key) => !options.hide.includes(key))

    let root

    try {
        root = element.shadowRoot || element.attachShadow({mode: 'open'})
    } catch (error) {
        console.warn('HubScrap: a form cannot be drawn in this element (use a div).')

        return false
    }

    element.setAttribute('data-hubscrap-bound', '')
    root.innerHTML = `<form class="scrap-local-form" novalidate aria-label="Get a quote"><div class="form-fields ${layout}">${keys.map(field).join('')}</div>` +
        `<div class="form-error" role="alert"></div>` +
        `<div class="form-controls ${layout}"><button type="submit">${SUBMIT_TEXT}</button></div></form>`

    style(root)

    const form = root.firstElementChild

    wear(form, options)
    form.addEventListener('submit', (event) => {
        event.preventDefault()
        submit(form, options)
    })
    emit(element, 'ready', options)

    return true
}

/** Draws a form in every element that asks for one with `data-hubscrap-form`. */
function scan(root = document) {
    root.querySelectorAll('[data-hubscrap-form]').forEach((element) => mount(element))
}

const HubScrap = {
    version: __LIB_VERSION__,
    boot(config = {}) {
        if (config.endpoint) settings.endpoint = String(config.endpoint).replace(/\/+$/, '')
        if (config.debug !== undefined) settings.debug = !!config.debug
        if (config.workspaceId) settings.workspaceId = String(config.workspaceId).trim()
        if (config.formId) settings.formId = String(config.formId).trim()
        if (config.brandId) settings.brandId = String(config.brandId).trim()
        if (config.affiliateId) settings.affiliateId = String(config.affiliateId).trim()
    },
    /** Draws a form in a selector's, an element's or a list of elements' place, from script instead of attributes. */
    addForm(target, options = {}) {
        const elements = typeof target === 'string' ? document.querySelectorAll(target) : target instanceof Element ? [target] : target

        return [...elements].map((element) => mount(element, options)).some(Boolean)
    },
    scan,
    /** Which tracking a form tells on this page now: `tag-manager`, `analytics` or `browser` (the browser events alone). */
    tracking,
    /** `analytics` when Google Analytics is on the page but left out because Tag Manager is told first; else null. */
    ignored,
    /** `push(['addForm', target, options])`: a call made the way a page makes it before the library has loaded. */
    push(...commands) {
        for (const [name, ...args] of commands) {
            if (['boot', 'addForm', 'scan'].includes(name)) this[name](...args)
            else log('unknown command', name)
        }

        return commands.length
    },
}

/** Looks again for forms to draw when something is added to the page, once however many things were. */
function watch() {
    if (typeof MutationObserver === 'undefined') return

    let queued = false
    const wanted = (node) => node.nodeType === 1 && (node.matches('[data-hubscrap-form]') || node.querySelector('[data-hubscrap-form]'))

    new MutationObserver((mutations) => {
        if (queued || !mutations.some((mutation) => [...mutation.addedNodes].some(wanted))) return

        queued = true
        requestAnimationFrame(() => {
            queued = false
            scan()
        })
    }).observe(document.documentElement, {childList: true, subtree: true})
}

// Loaded twice (a tag manager and the page, say), the first one is the library: the second does nothing.
if (!(window.HubScrap && window.HubScrap.version)) {
    // What the page asked for before the library arrived waits for the page, since loaded `async` it may be here before its forms are.
    const asked = Array.isArray(window.HubScrap) ? [...window.HubScrap] : []
    const start = () => {
        HubScrap.push(...asked)
        scan()
    }

    window.HubScrap = HubScrap
    watch()

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start)
    else start()
}
